/**
 * Store of the logical page navigation history, used by the application back button.
 *  - Fed by the global `router.afterEach` (src/plugins/router.ts) on each successful navigation.
 *  - The tab navigations inside a same page (same page identity) do not create a new entry.
 *  - The entries store the browser history positions managed by vue-router, allowing the back
 *    button to jump to the previous page atomically, ignoring the intermediate tab entries.
 *  - Persisted in `sessionStorage` so the back button still targets the previous page after a
 *    page reload (F5): the browser history stack and its positions survive a reload. A new
 *    browser tab does not share the `sessionStorage` and falls back to the route `backTo` meta.
 */
import { defineStore } from "pinia";
import type { RouteLocationNormalized } from "vue-router";
import type { PageHistoryEntry } from "~/core/models/PageHistoryEntry.interface";

const STORAGE_KEY = "lxc-page-history";

function loadFromStorage(): PageHistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    return raw ? (JSON.parse(raw) as PageHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function saveToStorage(pages: PageHistoryEntry[]) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pages));
  } catch {
    // `sessionStorage` may be unavailable (private mode, quota, etc.): the
    // store keeps working in-memory, only the reload survival is lost.
  }
}

export const usePageHistoryStore = defineStore("usePageHistoryStore", () => {
  const state = reactive<{ pages: PageHistoryEntry[] }>({
    pages: loadFromStorage(),
  });

  /**
   * Get the page identity of a route: the explicit `pageKey` route meta or,
   * by default, the path of the matched route record.
   */
  function getPageKey(to: RouteLocationNormalized): string {
    return (
      to.meta.pageKey ?? to.matched[to.matched.length - 1]?.path ?? to.path
    );
  }

  /**
   * Record a successful navigation to a route.
   * A tab navigation (same page identity as the last recorded page) is ignored.
   * A navigation to a position at or before the last recorded one (browser back or
   * `router.replace`) trims the forward entries, mirroring the browser history behavior.
   * @param to The route navigated to.
   * @param position The browser history position of the navigation.
   */
  function recordNavigation(to: RouteLocationNormalized, position: number) {
    const pageKey = getPageKey(to);

    if (state.pages.at(-1)?.pageKey === pageKey) {
      return;
    }

    state.pages = state.pages.filter((page) => page.position < position);
    state.pages.push({ pageKey, position, fullPath: to.fullPath });
    saveToStorage(state.pages);
  }

  /**
   * Get the previous page of a route in the logical history: the last
   * recorded page with a different identity, located before the given
   * browser history position. The tab navigations are naturally skipped
   * since they are not recorded.
   * @param to The current route.
   * @param position The current browser history position.
   * @returns The previous page entry, or undefined if there is none (first
   * page or new browser tab).
   */
  function getPreviousPage(
    to: RouteLocationNormalized,
    position: number,
  ): PageHistoryEntry | undefined {
    const pageKey = getPageKey(to);

    for (let index = state.pages.length - 1; index >= 0; index--) {
      const page = state.pages[index];
      if (page.position < position && page.pageKey !== pageKey) {
        return page;
      }
    }

    return undefined;
  }

  /**
   * Reset the page navigation history.
   */
  function reset() {
    state.pages = [];
    saveToStorage(state.pages);
  }

  return {
    ...toRefs(state),
    recordNavigation,
    getPreviousPage,
    reset,
  };
});
